/* Shopify owns products, inventory, cart, prices and checkout.
 * Stored data is a signed, limited shopping credential, not an authentication flag.
 * Every purchase reveal and submit is authorised by the Worker/parent My Film session.
 */
(() => {
  const endpoint = 'https://picture-this-stories.flat-base-8ca5.workers.dev/api/my-film/shopify/';
  const key = 'ptfl-my-film-shopping-v1';
  const staffKey = 'ptfl-my-film-staff-context';
  const logoutSession = window.ptflStaffLogout;
  delete window.ptflStaffLogout;
  // Story/review invitations have their own signed access. An unfinished staff
  // cart cleanup must not replace their form. Keep the pending marker and the
  // normal checkout guards; shopping pages still require the existing cleanup.
  const contributionForm = document.querySelector('[data-essay-form]');
  if (logoutSession && !contributionForm) { void finishPortalLogout(logoutSession); return; }
  const enabled = document.querySelector('#ptfl-shopping-bridge')?.dataset.enabled === 'true';
  let grant = '', status = null, refreshing = null, saving = false, staffCustomer = '', customerGrant = '', draftMessage = '';
  let staffContext = document.documentElement.classList.contains('ptfl-staff-shopping'), storageReady = true;
  const handoff = window.ptflCommerceHandoff;
  delete window.ptflCommerceHandoff;

  async function clearStaffCart(check = () => {}) {
    const root = window.Shopify?.routes?.root || '/';
    const request = async (path, body) => {
      check();
      const response = await fetch(root + path, { credentials: 'same-origin', cache: 'no-store',
        signal: AbortSignal.timeout(15000), ...(body === undefined ? {} : {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
        }) });
      if (!response.ok) throw Error('Shopify did not confirm that the cart was emptied.');
      const cart = await response.json(); check(); return cart;
    };
    const before = await request('cart.js');
    await request('cart/clear.js', {});
    const attributes = Object.fromEntries(Object.keys(before.attributes || {}).map(name => [name, '']));
    await request('cart/update.js', { note: '', attributes, discount: '' });
    const after = await request('cart.js');
    if (!Array.isArray(after.items) || after.items.length || after.item_count > 0 ||
        after.note || Object.values(after.attributes || {}).some(Boolean)) {
      throw Error('The cart is not empty yet. Close other shopping tabs and try again.');
    }
  }

  async function finishPortalLogout(session) {
    // Runs on Shopify's own origin after the Worker has revoked the portal
    // session. Never send a Shopify cart cookie through the Worker.
    const main = document.querySelector('main') || document.body;
    const message = document.createElement('p'), retry = document.createElement('button'), review = document.createElement('button'), clear = document.createElement('button');
    message.setAttribute('role', 'status'); retry.type = 'button'; retry.textContent = 'Retry clearing cart';
    review.type = 'button'; review.textContent = 'Review current shopping cart'; review.hidden = true;
    clear.type = 'button'; clear.textContent = 'Clear this cart and end staff shopping'; clear.hidden = true;
    review.onclick = () => {
      try {
        if (localStorage.getItem('ptfl-my-film-staff-logout') === session) localStorage.removeItem('ptfl-my-film-staff-logout');
        location.replace('/cart');
      } catch { message.textContent = 'Allow browser storage before reviewing the current cart.'; }
    };
    main.replaceChildren(message, retry, review, clear);
    let running = false, recoveryExpected = null;
    const finish = async (explicitClear = false) => {
      if (running) return;
      running = true; retry.disabled = true; message.textContent = 'Logged out of My Film. Clearing the staff shopping cart…';
      try {
        localStorage.setItem('ptfl-my-film-staff-logout', session);
        const expected = localStorage.getItem(key) || '';
        const expectedStaff = localStorage.getItem(staffKey);
        const unchanged = () => {
          if ((localStorage.getItem(key) || '') !== expected || localStorage.getItem(staffKey) !== expectedStaff ||
              localStorage.getItem('ptfl-my-film-staff-logout') !== session) {
            throw Error('The shopping session changed. Check the cart before the next customer.');
          }
        };
        let payload;
        try { payload = JSON.parse(atob(expected.split('.')[0].replace(/-/g, '+').replace(/_/g, '/'))); } catch {}
        if (!expected && localStorage.getItem(staffKey) !== 'true') {
          // No staff shopping took place on this origin. Leave any personal cart alone.
          localStorage.removeItem('ptfl-my-film-staff-logout');
          location.replace('/login'); return;
        }
        if (payload?.mode !== 'staff' || payload.sid !== session) {
          // An old logout marker is not permission to clear a newer cart. Read
          // only: an already-empty cart needs no destructive cleanup at all.
          const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', {
            credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000),
          });
          if (!response.ok) throw Error('Could not check the current shopping cart. Please try again.');
          const cart = await response.json(); unchanged();
          const empty = Array.isArray(cart.items) && cart.items.length === 0 && cart.item_count === 0 &&
            !cart.note && !Object.values(cart.attributes || {}).some(Boolean) &&
            !(cart.discount_codes || []).some(code => code.applicable !== false) &&
            !(cart.cart_level_discount_applications || []).length;
          if (empty && !explicitClear) {
            // Preserve any newer shopping credential; retire only this logout.
            localStorage.removeItem('ptfl-my-film-staff-logout');
            location.replace('/login'); return;
          }
          if (!explicitClear) {
            review.hidden = false;
            clear.hidden = false;
            recoveryExpected = { grant: expected, staff: expectedStaff };
            throw Error('This browser has a different shopping session. Review its cart, or clear this cart and end staff shopping before the next customer.');
          }
        }
        await clearStaffCart(unchanged);
        unchanged();
        localStorage.removeItem(key); localStorage.removeItem(staffKey);
        localStorage.removeItem('ptfl-my-film-staff-logout');
        localStorage.setItem('ptfl-my-film-logout', crypto.randomUUID());
        location.replace('/login');
      } catch (error) {
        message.textContent = 'My Film is logged out, but cart cleanup is not complete. ' + error.message;
        retry.disabled = false; running = false;
      }
    };
    retry.onclick = () => finish();
    clear.onclick = () => {
      try {
        if (!recoveryExpected || (localStorage.getItem(key) || '') !== recoveryExpected.grant ||
            localStorage.getItem(staffKey) !== recoveryExpected.staff || localStorage.getItem('ptfl-my-film-staff-logout') !== session) {
          message.textContent = 'The shopping session changed. Review the current cart before clearing it.';
          clear.hidden = true; return;
        }
        void finish(true);
      } catch { message.textContent = 'Allow browser storage before clearing the current cart.'; }
    };
    // The page stays locked if cleanup fails. It must never offer normal checkout.
    window.addEventListener('submit', event => { event.preventDefault(); event.stopImmediatePropagation(); }, true);
    await finish();
  }
  try {
    grant = enabled ? (handoff || localStorage.getItem(key) || '') : '';
    if (enabled && handoff) localStorage.setItem(key, handoff);
    if (!enabled && !staffContext) localStorage.removeItem(key);
  } catch { storageReady = false; }

  function paint() {
    if (status?.staffMode) {
      staffContext = true;
      document.documentElement.classList.add('ptfl-staff-shopping');
      try { localStorage.setItem(staffKey, 'true'); } catch { storageReady = false; }
    }
    const allowed = status?.filmPurchasing === true && (!staffContext || storageReady);
    document.querySelectorAll('[data-ptfl-film-purchase]').forEach(element => {
      element.hidden = !allowed;
      element.style.display = allowed ? '' : 'none';
    });
    document.querySelectorAll('[data-ptfl-film-discount]').forEach(element => { element.hidden = !allowed || staffContext; });
    document.querySelectorAll('[data-ptfl-pos-panel]').forEach(panel => {
      panel.hidden = !staffContext;
      const customer = panel.querySelector('[data-ptfl-pos-customer]');
      const copy = !storageReady ? 'Allow browser storage and reopen staff My Film before shopping.' :
        status?.staffMode && staffCustomer ? 'Customer: ' + staffCustomer : 'Reopen the customer’s staff My Film link to continue. Online checkout stays disabled.';
      if (customer.textContent !== copy) customer.textContent = copy;
      panel.querySelector('[data-ptfl-pos-create]').disabled = saving || !status?.staffMode || !staffCustomer || !storageReady;
      const message = panel.querySelector('[data-ptfl-pos-result]');
      if (message.textContent !== draftMessage) message.textContent = draftMessage;
    });
  }
  async function api(path, body) {
    if (!enabled || !grant) throw new Error('Please open Buy film or Develop again from My Film.');
    const response = await fetch(endpoint + path, {
      method: body === undefined ? 'GET' : 'POST', credentials: 'omit', cache: 'no-store',
      headers: { Authorization: 'Bearer ' + grant, ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }), signal: AbortSignal.timeout(path === 'pos-draft' ? 90000 : 15000),
    });
    const result = await response.json();
    if (!response.ok) {
      // A revoked staff grant has no authority, but its session ID is still
      // needed to match the subsequent cross-origin logout/cart cleanup.
      if ((response.status === 401 || response.status === 404) && !staffContext) {
        grant = ''; try { localStorage.removeItem(key); } catch {}
      }
      const error = new Error((result.error || 'Please reopen My Film to continue.') + (result.reference ? ' Reference: ' + result.reference : ''));
      error.definiteRejection = [400, 401, 403, 404, 409].includes(response.status) && !result.reference;
      throw error;
    }
    return result;
  }
  function refresh() {
    if (refreshing) return refreshing;
    // Hide immediately while revalidating; a stale browser flag never reveals controls.
    status = null; paint();
    refreshing = api('session').then(async result => {
      status = result;
      if (result.staffMode && customerGrant !== grant) {
        staffCustomer = ''; paint();
        const checkedGrant = grant;
        const customer = await api('staff-customer', {});
        if (grant === checkedGrant) { staffCustomer = customer.email; customerGrant = grant; }
      }
      return result;
    })
      .catch(() => null).finally(() => { refreshing = null; paint(); });
    return refreshing;
  }
  function notice(text) {
    let element = document.querySelector('#ptfl-shopping-status');
    if (!element) {
      element = document.createElement('p'); element.id = 'ptfl-shopping-status';
      element.setAttribute('role', 'status');
      element.style.cssText = 'padding:20px;margin:16px;max-width:60ch';
      (document.querySelector('main') || document.body).prepend(element);
    }
    element.textContent = text;
  }

  // The acknowledgement also protects ordinary visitors who already have film
  // in a cart. It is a UX safeguard, not a Shopify checkout validation function.
  function confirmCollection() {
    const unchecked = [...document.querySelectorAll('[data-ptfl-collection-confirm]')]
      .find(input => input.getClientRects().length && !input.checked);
    if (!unchecked) return true;
    unchecked.reportValidity(); unchecked.focus();
    return false;
  }
  window.addEventListener('click', event => {
    if (event.target.closest('[data-ptfl-pos-create]')) { event.preventDefault(); createPosDraft(event.target.closest('[data-ptfl-pos-panel]')); return; }
    if (event.target.closest('[data-ptfl-pos-end]')) { event.preventDefault(); endStaffShopping(); return; }
    if (staffContext && event.target.closest('[name="checkout"],a[href^="/checkout"],.shopify-payment-button,.additional-checkout-buttons,shopify-accelerated-checkout,shopify-accelerated-checkout-cart')) {
      event.preventDefault(); event.stopImmediatePropagation(); notice('Staff-assisted orders use Create draft for POS in the cart.'); return;
    }
    if (!event.target.closest('[name="checkout"]')) return;
    if (!confirmCollection()) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }, true);
  const authorisedSubmits = new WeakSet();
  // Horizon delegates on:submit at document capture. Window capture must run
  // first regardless of which deferred/module asset loaded first.
  window.addEventListener('submit', async event => {
    const form = event.target;
    if ((event.submitter?.name === 'checkout' || (!event.submitter && form.id === 'cart-form')) && !confirmCollection()) {
      event.preventDefault(); event.stopImmediatePropagation(); return;
    }
    if (staffContext && (event.submitter?.name === 'checkout' || (!event.submitter && form.id === 'cart-form'))) {
      event.preventDefault(); event.stopImmediatePropagation(); notice('Use Create draft for POS after reviewing this customer’s cart.'); return;
    }
    if (!form.closest('[data-ptfl-film-purchase]')) return;
    if (authorisedSubmits.delete(form)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    const submitter = event.submitter;
    const current = await refresh();
    if (!current?.filmPurchasing || (staffContext && !storageReady) || !form.isConnected) {
      notice('Please reopen Buy film in My Film to purchase film for collection.'); return;
    }
    authorisedSubmits.add(form);
    form.requestSubmit(submitter?.isConnected ? submitter : undefined);
  }, true);
  new MutationObserver(paint).observe(document.body, { childList: true, subtree: true });
  window.addEventListener('pageshow', refresh);
  window.addEventListener('focus', refresh);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refresh(); });
  window.addEventListener('storage', event => {
    if (event.key === 'ptfl-my-film-logout' && event.newValue) { location.replace('/login'); return; }
    if (event.key === staffKey && event.newValue === 'true') {
      staffContext = true; document.documentElement.classList.add('ptfl-staff-shopping'); paint();
    }
    if (event.key !== key) return;
    staffCustomer = ''; customerGrant = ''; draftMessage = '';
    document.querySelectorAll('[data-ptfl-collection-confirm]').forEach(input => { input.checked = false; });
    grant = enabled ? event.newValue || '' : ''; refresh();
  });
  if (enabled) setInterval(() => { if (!document.hidden && grant) refresh(); }, 30000);
  refresh();

  async function createPosDraft(panel) {
    if (saving || !staffContext || !storageReady) return;
    if (!confirmCollection()) { draftMessage = 'Please confirm that the retail film must be collected from Brighton.'; paint(); return; }
    saving = true; draftMessage = 'Checking the customer and current Shopify cart…'; paint();
    const expectedGrant = grant;
    try {
      const current = await refresh();
      if (!current?.staffMode || !staffCustomer || expectedGrant !== grant) throw new Error('The customer session changed. Reopen staff My Film and review the cart.');
      const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000) });
      if (!response.ok) throw new Error('Could not read the current Shopify cart.');
      const cart = await response.json();
      if (expectedGrant !== grant) throw new Error('The customer session changed. Review the cart again.');
      if (!confirmCollection()) { draftMessage = 'Please confirm that the retail film must be collected from Brighton.'; return; }
      const submittedCustomer = staffCustomer;
      const result = await api('pos-draft', { currency: cart.currency, confirmed: true,
        collectionConfirmed: [...document.querySelectorAll('[data-ptfl-collection-confirm]')].some(input => input.getClientRects().length && input.checked),
        note: cart.note, attributes: cart.attributes, discountCodes: cart.discount_codes,
        items: cart.items.map(item => ({ id: String(item.variant_id), quantity: item.quantity, properties: item.properties, sellingPlan: item.selling_plan_allocation?.selling_plan?.id })) });
      const total = result.draft.total;
      draftMessage = 'Unpaid draft ' + result.draft.name + (total ? ' · ' + total.currencyCode + ' ' + total.amount : '') +
        (result.reused ? ' already saved for ' : ' saved for ') + submittedCustomer + '. Open it from Draft orders in Shopify POS. No payment was taken and no invoice was sent by My Film. This cart is retained; saving the same cart again returns this draft. End staff shopping when finished.';
    } catch (error) { draftMessage = error.message + (error.definiteRejection ? '' : ' If saving was interrupted, keep the cart unchanged and check Shopify Drafts before trying again.'); }
    finally { saving = false; paint(); }
  }
  async function endStaffShopping() {
    if (saving || !staffContext || !confirm('Empty this Shopify cart and end staff shopping? Any saved Shopify draft is kept.')) return;
    saving = true; paint();
    try {
      const expectedGrant = grant;
      await clearStaffCart(() => { if (grant !== expectedGrant) throw Error('The customer session changed. Check the cart before continuing.'); });
      localStorage.removeItem(key); localStorage.removeItem(staffKey);
      localStorage.removeItem('ptfl-my-film-staff-logout');
      location.reload();
    } catch (error) { draftMessage = error.message; saving = false; paint(); }
  }

  async function addReorder() {
    if (!enabled || !handoff || location.pathname.replace(/\/$/, '') !== '/cart') return;
    if (staffContext && !storageReady) { notice('Allow browser storage and reopen staff My Film before adding developing.'); return; }
    const marker = key + ':used:' + handoff.slice(-43);
    try {
      if (sessionStorage.getItem(marker)) return;
      sessionStorage.setItem(marker, 'started');
    } catch {
      notice('Please allow browser storage before using Develop again. Your cart has not changed.'); return;
    }
    let adding = false;
    try {
      notice('Checking your previous developing options…');
      const result = await api('reorder-items', {});
      adding = true;
      const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: result.items }), signal: AbortSignal.timeout(20000),
      });
      const added = await response.json();
      if (!response.ok) throw new Error(added.description || 'Shopify could not add this developing option.');
      // Reload the normal cart, retaining every existing item. Never replace it.
      location.replace((window.Shopify?.routes?.root || '/') + 'cart');
    } catch (error) {
      notice(adding ? 'Please check your cart before trying again: Shopify did not confirm the addition. ' + error.message
        : error.message + ' View current developing options from the Film Lab menu.');
    }
  }
  addReorder();
})();
